import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import { Transaction, TransactionFormData } from '@/types';
import { useAuth } from '../AuthContext';
import { useProfile } from '../ProfileContext';
import { useEncryption } from '../EncryptionContext';
import { TransactionsContextType } from './types';
import { loadTransactionsFromSupabase } from '@/lib/supabase-operations';
import { transactionsReducer, initialState } from './reducer';
import { useTransactionSelectors } from './selectors';
import { 
  addTransactionOperation, 
  updateTransactionOperation, 
  deleteTransactionOperation 
} from './operations';
import { 
  processBillPayment as processBillPaymentFIFO, 
  getBillPaymentStatus as getBillPaymentStatusFn 
} from './billPaymentOperations';
import { logger } from '@/utils/logger';

const TransactionsContext = createContext<TransactionsContextType | undefined>(undefined);

export const TransactionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(transactionsReducer, initialState);
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const { isEncryptionReady, isCheckingEncryption } = useEncryption();

  const selectors = useTransactionSelectors(state.transactions);

  // Load transactions from Supabase
  useEffect(() => {
    if (!user) return;
    
    if (isCheckingEncryption) {
      logger.debug('Waiting for encryption to be ready...');
      return;
    }
    
    if (!isEncryptionReady) {
      logger.warn('Encryption not ready, skipping load');
      dispatch({ type: 'SET_ERROR', payload: 'Sistema de criptografia não disponível' });
      dispatch({ type: 'SET_LOADING', payload: false });
      return;
    }

    const loadTransactions = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      
      try {
        logger.info('Loading transactions from Supabase');
        const supabaseTransactions = await loadTransactionsFromSupabase();
        dispatch({ type: 'SET_TRANSACTIONS', payload: supabaseTransactions });
        logger.info('Transactions loaded successfully', { count: supabaseTransactions.length });
      } catch (err) {
        logger.error('Error loading transactions', { error: err });
        
        let errorMessage = 'Erro ao carregar transações';
        if (err instanceof Error) {
          if (err.message === 'Decryption failed') {
            errorMessage = 'Erro ao descriptografar dados. Verifique a configuração da chave de criptografia no Vault.';
          } else {
            errorMessage = err.message;
          }
        }
        
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        dispatch({ type: 'CLEAR_TRANSACTIONS' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    loadTransactions();
  }, [user, isEncryptionReady, isCheckingEncryption]);

  // Reload when active profile changes
  useEffect(() => {
    if (user && activeProfile) {
      logger.debug('Profile changed, reloading transactions', { profileId: activeProfile.id });
      
      const loadTransactions = async () => {
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_ERROR', payload: null });
        
        try {
          const supabaseTransactions = await loadTransactionsFromSupabase();
          dispatch({ type: 'SET_TRANSACTIONS', payload: supabaseTransactions });
          logger.info('Transactions reloaded for profile', { 
            profileName: activeProfile.name, 
            count: supabaseTransactions.length 
          });
        } catch (err) {
          logger.error('Error reloading transactions', { error: err });
          
          let errorMessage = 'Erro ao carregar transações';
          if (err instanceof Error) {
            if (err.message === 'Decryption failed') {
              errorMessage = 'Erro ao descriptografar dados. Verifique a configuração da chave de criptografia no Vault.';
            } else {
              errorMessage = err.message;
            }
          }
          
          dispatch({ type: 'SET_ERROR', payload: errorMessage });
          dispatch({ type: 'CLEAR_TRANSACTIONS' });
        } finally {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      };
      
      loadTransactions();
    }
  }, [activeProfile?.id, user]);

  const addTransaction = useCallback(async (transactionData: TransactionFormData): Promise<Transaction> => {
    return addTransactionOperation(transactionData, dispatch);
  }, []);

  // Listen for initial balance transaction events
  useEffect(() => {
    const handleInitialBalanceTransaction = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const transactionData = customEvent.detail;
      logger.debug('Handling initial balance transaction event', { data: transactionData });
      
      try {
        await addTransaction(transactionData);
        logger.info('Initial balance transaction created successfully');
      } catch (error) {
        logger.error('Error creating initial balance transaction', { error });
      }
    };

    const handleDebtTransactionsCreated = async (event: Event) => {
      const customEvent = event as CustomEvent;
      logger.info('Debt transactions created, reloading all transactions', { detail: customEvent.detail });
      
      try {
        const supabaseTransactions = await loadTransactionsFromSupabase();
        dispatch({ type: 'SET_TRANSACTIONS', payload: supabaseTransactions });
        logger.info('Transactions reloaded after debt creation', { count: supabaseTransactions.length });
      } catch (error) {
        logger.error('Error reloading transactions after debt creation', { error });
      }
    };

    window.addEventListener('create-initial-balance-transaction', handleInitialBalanceTransaction);
    window.addEventListener('debt-transactions-created', handleDebtTransactionsCreated);
    
    return () => {
      window.removeEventListener('create-initial-balance-transaction', handleInitialBalanceTransaction);
      window.removeEventListener('debt-transactions-created', handleDebtTransactionsCreated);
    };
  }, [addTransaction]);

  // Listen for account deletion events to remove orphaned transactions from local state
  useEffect(() => {
    const handleAccountDeleted = (event: Event) => {
      const customEvent = event as CustomEvent<{ accountId: string }>;
      const { accountId } = customEvent.detail;
      
      logger.info('Account deleted event received, filtering transactions', { accountId });
      
      dispatch({ 
        type: 'SET_TRANSACTIONS', 
        payload: state.transactions.filter(t => t.accountId !== accountId) 
      });
    };

    window.addEventListener('account-deleted', handleAccountDeleted);
    return () => window.removeEventListener('account-deleted', handleAccountDeleted);
  }, [state.transactions]);

  const updateTransaction = useCallback(async (id: string, updates: Partial<Transaction>) => {
    const result = await updateTransactionOperation(id, updates, state.transactions, dispatch);
    
    // If status was updated, emit event for installment sync
    if ('status' in updates) {
      const transaction = state.transactions.find(t => t.id === id);
      if (transaction) {
        window.dispatchEvent(new CustomEvent('transaction-status-changed', {
          detail: { 
            transactionId: id, 
            newStatus: updates.status,
            paidDate: new Date().toISOString().split('T')[0]
          }
        }));
        logger.debug('Transaction status change event dispatched', { transactionId: id, newStatus: updates.status });
      }
    }
    
    return result;
  }, [state.transactions]);

  const deleteTransaction = useCallback(async (id: string) => {
    return deleteTransactionOperation(id, dispatch);
  }, []);

  // Implementação real do processBillPayment usando lógica FIFO
  // CORRIGIDO: Removida duplicação de salvamento - o batch save já é feito em billPaymentOperations
  const processBillPayment = useCallback(async (
    cardId: string,
    cardName: string,
    amount: number,
    accountId: string,
    date: string,
    billTransactions: Transaction[],
    onPaymentSummary?: (summary: any) => void,
    releaseCardLimit?: (cardId: string, amount: number) => void,
    onProgress?: (progress: any) => void
  ): Promise<boolean> => {
    try {
      if (!releaseCardLimit) {
        logger.error('Missing releaseCardLimit function for bill payment');
        return false;
      }
      
      logger.info('Processing bill payment', { cardId, amount, accountId });
      
      const result = await processBillPaymentFIFO({
        cardId,
        cardName,
        amount,
        accountId,
        date,
        billTransactions,
        transactions: state.transactions,
        releaseCardLimit,
        setTransactions: (updater) => {
          // APENAS atualiza o estado local - salvamento já foi feito no batch
          const updatedTransactions = typeof updater === 'function' 
            ? updater(state.transactions) 
            : updater;
          
          dispatch({ type: 'SET_TRANSACTIONS', payload: updatedTransactions });
        },
        onPaymentSummary,
        onProgress
      });
      
      logger.info('Bill payment processed', { success: result });
      return result;
    } catch (error) {
      logger.error('Error processing bill payment', { error });
      throw error; // Re-throw para o modal de processamento tratar
    }
  }, [state.transactions]);

  // Implementação real do getBillPaymentStatus
  const getBillPaymentStatus = useCallback((cardId: string, billPeriod: string) => {
    return getBillPaymentStatusFn(state.transactions, cardId, billPeriod);
  }, [state.transactions]);

  const value: TransactionsContextType = useMemo(() => ({
    transactions: state.transactions,
    isLoading: state.isLoading,
    error: state.error,
    addTransaction,
    updateTransaction,
    deleteTransaction,
    getTransactionsByCard: selectors.getByCard,
    getTransactionsByAccount: selectors.getByAccount,
    getRecentTransactions: selectors.getRecent,
    processBillPayment,
    getBillPaymentStatus,
    createDebtBalanceForCard: async () => {},
  }), [
    state.transactions, 
    state.isLoading, 
    state.error, 
    addTransaction, 
    updateTransaction, 
    deleteTransaction,
    selectors,
    processBillPayment,
    getBillPaymentStatus
  ]);

  return (
    <TransactionsContext.Provider value={value}>
      {children}
    </TransactionsContext.Provider>
  );
};

export const useTransactions = () => {
  const context = useContext(TransactionsContext);
  if (!context) {
    throw new Error('useTransactions must be used within a TransactionsProvider');
  }
  return context;
};
